/*
 * The capability pill: the ONLY thing on a band or sub-header that links out.
 *
 * FOUR RULES THIS COMPONENT EXISTS TO ENFORCE, from direction §4, §9 and build
 * rule 5. They are enforced by the type signature rather than by documentation,
 * because a rule a component merely describes is a rule the next caller breaks.
 *
 *   1. A LABEL MAPS ONE-TO-ONE TO A DESTINATION KIND. "View" opens live FTL
 *      surfaces including FTL results pages; "Details" opens tournament
 *      info/registration pages; "Results" opens AskFRED results; a pool pill
 *      names its destination ("Pool 5"). Same word, same kind, everywhere. The
 *      `kind` prop is the label — a caller cannot pass arbitrary text.
 *
 *   2. PRESENCE SIGNALS A REAL CAPABILITY (principle 4). `href` is required and
 *      non-nullable. A caller with no URL renders nothing, and the way to do
 *      that is not to render this component. That is why there is no `disabled`
 *      state: a disabled capability pill would claim a capability while denying
 *      it.
 *
 *   3. NO EXTERNAL-LINK ICONS ANYWHERE (§9). Red plus the button shape IS the
 *      affordance; "leaves the app" is carried by learned consistency, which the
 *      navigation contract guarantees. It also makes RTL simpler, since there is
 *      no glyph whose side has to flip.
 *
 *   4. A REAL ANCHOR (principle 1, build rule 10). An `<a href>` with a real
 *      URL, so cmd/middle-click open in a new tab, the native context menu
 *      offers "Copy link address", and the back button is untouched. No onClick
 *      navigation, ever.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import styles from "./CapabilityPill.module.css";

/**
 * The destination kinds. Closed on purpose: adding one is a change to the voice
 * rules (§4 keeps a registry of them), not a prop a screen may invent.
 *
 * `fie` and `efc` (decision 948) are an international tournament's own page
 * on its governing body's site, beneath Details, labelled with the body's name
 * as the catalogue spells it ("FIE", "EFC") -- the same word the feed uses
 * for that source, so one source has one name everywhere.
 */
export type CapabilityKind = "view" | "details" | "results" | "pool" | "fie" | "efc";

export interface CapabilityPillProps {
  kind: CapabilityKind;

  /** Required: see rule 2. There is no pill without a destination. */
  href: string;

  /** The pool number. Required for `kind="pool"`, meaningless otherwise. */
  poolNumber?: number;

  /**
   * Extra context for assistive technology, when the visible label alone is
   * ambiguous out of order — e.g. "View" repeated down a list of events reads as
   * "View, View, View" without it.
   *
   * Real DOM text elsewhere on the row is preferred where possible; this is for
   * the cases where the row's text is visually adjacent but not programmatically
   * associated.
   */
  describedBy?: string;
}

export function CapabilityPill({
  kind,
  href,
  poolNumber,
  describedBy,
}: CapabilityPillProps) {
  const { t } = useTranslation();

  const label =
    kind === "pool"
      ? t("pill.pool", { number: poolNumber })
      : kind === "fie" || kind === "efc"
        ? t(`source.${kind}`)
        : t(`pill.${kind}`);

  return (
    <a
      className={styles.pill}
      href={href}
      // Opens the source's own site. `noreferrer` implies `noopener`, and both
      // are here because these are third-party pages we do not control.
      target="_blank"
      rel="noreferrer"
      aria-describedby={describedBy}
    >
      {/* The pool number is data, so it takes tabular figures — a column of
          pool pills should not jitter between "Pool 2" and "Pool 11". */}
      <span className={kind === "pool" ? "tc-num" : undefined}>{label}</span>
    </a>
  );
}

/**
 * More than one pill in one slot, one above the other at one width: a
 * tournament's Details with its FIE or EFC page beneath (948). Each pill keeps
 * every rule above; the stack only places them.
 */
export function CapabilityPills({ children }: { children: ReactNode }) {
  return <span className={styles.stack}>{children}</span>;
}
